import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  FormHelperText,
  IconButton,
  FormControlLabel,
  Checkbox,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  CircularProgress,
} from '@mui/material';
import { Visibility, VisibilityOff, Close as CloseIcon, Refresh as RefreshIcon, Security as SecurityIcon } from '@mui/icons-material';
import { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth, pickLoginVerificationStatus, getLoginSuccessToastMessage } from '../context/AuthContext';
import {
  signup as signupApi,
  sendSignupOtps,
  getLatestPrivacy,
  getLatestTerms,
} from '../api';
import { toast } from 'react-toastify';
import logo from '../assets/logo1.png';
import { categoriesAPI } from '../services/api';
import { persistAuthCredentials } from '../utils/secureStorage';

const SIGNUP_EMAIL_MAX = 254;
const SIGNUP_EMAIL_LOCAL_MAX = 64;

const generateCaptchaCode = () => {
  const chars = '23456789ABCDEFGHJKLMNPQRSTUVWXYZ';
  let result = '';
  for (let i = 0; i < 6; i += 1) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return result;
};

const CaptchaCanvas = ({ code, onRefresh }) => {
  const canvasRef = useRef(null);

  const drawCaptcha = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const width = canvas.width;
    const height = canvas.height;

    // Light ice-blue background matching reference UI image
    const grad = ctx.createLinearGradient(0, 0, width, height);
    grad.addColorStop(0, '#EAEFF4');
    grad.addColorStop(1, '#E1E7EE');
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, width, height);

    // Random dark noise dots
    for (let i = 0; i < 30; i += 1) {
      ctx.fillStyle = `rgba(51, 65, 85, ${0.15 + Math.random() * 0.25})`;
      ctx.beginPath();
      ctx.arc(Math.random() * width, Math.random() * height, Math.random() * 1.8, 0, Math.PI * 2);
      ctx.fill();
    }

    // Orange strike-through & intersecting lines crossing through text
    const lineColors = ['#FF8C42', '#E06526', '#FF9F5B'];
    for (let i = 0; i < 4; i += 1) {
      ctx.strokeStyle = lineColors[i % lineColors.length];
      ctx.lineWidth = 1.2;
      ctx.beginPath();
      ctx.moveTo(Math.random() * width, Math.random() * height);
      ctx.lineTo(Math.random() * width, Math.random() * height);
      ctx.stroke();
    }

    // Text rendering: alternating Dark Slate (#1E293B) and Orange (#FF8C42)
    const letterSpacing = width / (code.length + 0.6);

    ctx.textBaseline = 'middle';
    ctx.font = 'bold 18px "Segoe UI", Roboto, sans-serif';

    for (let i = 0; i < code.length; i += 1) {
      const char = code[i];
      const x = (i + 0.65) * letterSpacing;
      const y = height / 2 + (Math.random() * 2 - 1);
      const angle = Math.random() * 0.16 - 0.08;

      ctx.save();
      ctx.translate(x, y);
      ctx.rotate(angle);
      ctx.fillStyle = i % 2 === 0 ? '#1E293B' : '#FF8C42';
      ctx.fillText(char, -6, 0);
      ctx.restore();
    }
  }, [code]);

  useEffect(() => {
    drawCaptcha();
  }, [drawCaptcha]);

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      <Box
        sx={{
          border: '1px solid #CBD5E1',
          borderRadius: '8px',
          overflow: 'hidden',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '38px',
          width: '125px',
          backgroundColor: '#EAEFF4',
          userSelect: 'none',
        }}
      >
        <canvas
          ref={canvasRef}
          width={125}
          height={38}
          style={{ display: 'block' }}
        />
      </Box>
      <IconButton
        onClick={onRefresh}
        title="Refresh CAPTCHA"
        size="small"
        sx={{
          color: '#FF8C42',
          p: 0.5,
          '&:hover': {
            backgroundColor: 'transparent',
            opacity: 0.85,
          },
        }}
      >
        <RefreshIcon sx={{ fontSize: '1.4rem' }} />
      </IconButton>
    </Box>
  );
};


const validatePassword = (password) => {
  if (!password) return 'required';
  if (password.length > 12) return 'tooLong';
  if (password.length < 8) return 'tooShort';
  const hasUpper = /[A-Z]/.test(password);
  const hasLower = /[a-z]/.test(password);
  const hasNumber = /\d/.test(password);
  const hasSpecial = /[@$!%*?&#]/.test(password);
  if (!hasUpper || !hasLower || !hasNumber || !hasSpecial) return 'weak';
  return null;
};

/**
 * Signup email validation: RFC-oriented checks + HTML5-style pattern.
 * @returns {null | 'required' | 'tooLong' | 'invalid'} null = valid (caller handles empty separately if needed)
 */
const validateSignupEmail = (trimmed) => {
  if (!trimmed) return 'required';
  if (trimmed.length > SIGNUP_EMAIL_MAX) return 'tooLong';
  if (trimmed.includes(' ') || /[\r\n<>]/.test(trimmed)) return 'invalid';

  const atCount = (trimmed.match(/@/g) || []).length;
  if (atCount !== 1) return 'invalid';

  const [local, domain] = trimmed.split('@');
  if (!local || !domain) return 'invalid';
  if (local.length > SIGNUP_EMAIL_LOCAL_MAX) return 'invalid';
  if (local.startsWith('.') || local.endsWith('.') || local.includes('..')) return 'invalid';
  if (domain.startsWith('.') || domain.endsWith('.') || domain.includes('..')) return 'invalid';

  const labels = domain.split('.');
  if (labels.length < 2) return 'invalid';
  for (let i = 0; i < labels.length; i += 1) {
    const label = labels[i];
    if (!label || label.length > 63) return 'invalid';
    if (label.startsWith('-') || label.endsWith('-')) return 'invalid';
    if (!/^[a-zA-Z0-9-]+$/.test(label)) return 'invalid';
  }
  const tld = labels[labels.length - 1];
  if (tld.length < 2) return 'invalid';

  // HTML living standard “valid email address” pattern (subset for ASCII emails)
  const html5Email =
    /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
  if (!html5Email.test(trimmed)) return 'invalid';

  return null;
};

const normalizeServiceType = (contactType) => {
  if (contactType === '' || contactType === null || contactType === undefined) {
    return null;
  }
  return Number(contactType);
};

const buildSignupPayload = (isCompany, signupData, fullPhone, serviceTypeValue) => {
  const sharedFields = {
    email: signupData.email.trim(),
    phoneNumber: fullPhone,
    serviceType: serviceTypeValue,
    categoryId: serviceTypeValue,
    country: signupData.country,
    password: signupData.password,
    termsAndConditionsAccepted: Boolean(signupData.termsAndConditionsAccepted),
    dataProcessingConsent: Boolean(signupData.dataProcessingConsent),
  };

  if (isCompany) {
    return {
      accountType: 'Company',
      businessName: signupData.businessName.trim(),
      ...sharedFields,
    };
  }

  return {
    accountType: 'Freelancer',
    firstName: signupData.firstName.trim(),
    lastName: signupData.lastName.trim(),
    ...sharedFields,
  };
};

const buildSignupSessionData = (isCompany, signupData, serviceTypeValue, phoneNumber) => ({
  accountType: isCompany ? 'Company' : 'Freelancer',
  ...signupData,
  serviceType: serviceTypeValue,
  categoryId: serviceTypeValue,
  phoneNumber,
  name: isCompany
    ? signupData.businessName.trim()
    : `${signupData.firstName} ${signupData.lastName}`.trim(),
});

const extractSignupApiErrorMessage = (apiErr) =>
  apiErr?.response?.data?.error?.message ||
  apiErr?.response?.data?.message ||
  apiErr?.response?.data?.errorMessage ||
  apiErr?.message ||
  'Signup failed';

const resolveLoginErrorMessage = (err) => {
  const data = err.response?.data;
  const status = err.response?.status;
  return (
    data?.error?.message ||
    (typeof data?.error === 'string' ? data.error : '') ||
    data?.message ||
    data?.errorMessage ||
    (status === 401 ? 'Email does not exist or password is invalid.' : '') ||
    err.message ||
    'Login failed'
  );
};

const redirectExistingAccountToLogin = (setTab, setLoginForm) => {
  const existsMsg = 'Account already exists. Please login.';
  setTab('login');
  setLoginForm({ email: '', password: '' });
  toast.error(existsMsg);
};

const processExistingUserSignupResponse = (
  signupRespData,
  { isCompany, signupData, serviceTypeValue, navigate, setTab, setLoginForm }
) => {
  if (!(signupRespData?.success === true && signupRespData?.userExists === true)) {
    return false;
  }

  const msg = signupRespData?.message || 'Account already exists. Please login.';
  const nextStep = String(signupRespData?.data?.nextStep || '').toUpperCase();
  const verificationRequired = signupRespData?.data?.verificationRequired === true;
  const email = signupData.email.trim();
  const cleanPhone = signupData.phone.replace(/\D/g, '').replace(/^0+/, '');
  const phone = `+49${cleanPhone}`;
  const existingUserData = signupRespData?.user || signupRespData?.data || null;

  persistAuthCredentials({
    ...(signupRespData?.token ? { token: signupRespData.token } : {}),
    ...(existingUserData ? { user: existingUserData } : {}),
  });

  sessionStorage.setItem(
    'signupData',
    JSON.stringify(buildSignupSessionData(isCompany, signupData, serviceTypeValue, phone))
  );

  if (verificationRequired && nextStep === 'VERIFY_EMAIL') {
    toast.info(msg);
    navigate(`/verify?step=email&email=${encodeURIComponent(email)}&phone=${encodeURIComponent(phone)}`);
    return true;
  }
  if (verificationRequired && nextStep === 'VERIFY_PHONE') {
    toast.info(msg);
    navigate(`/verify?step=phone&email=${encodeURIComponent(email)}&phone=${encodeURIComponent(phone)}`);
    return true;
  }
  if (verificationRequired && nextStep === 'DOCUMENT_VERIFICATION') {
    toast.info('Please fill up documents to continue.');
    navigate('/account-verification');
    return true;
  }

  redirectExistingAccountToLogin(setTab, setLoginForm);
  return true;
};

const submitSignupRequest = async (payload) => {
  try {
    const resp = await signupApi(payload);
    return { signupRespData: resp?.data || null, errorMessage: null };
  } catch (apiErr) {
    console.warn('Signup API:', apiErr?.response?.data || apiErr.message);
    return { signupRespData: null, errorMessage: extractSignupApiErrorMessage(apiErr) };
  }
};

const completeNewUserSignup = async ({
  signupRespData,
  signupData,
  isCompany,
  serviceTypeValue,
  fullPhone,
  navigate,
}) => {
  persistAuthCredentials({
    ...(signupRespData?.token ? { token: signupRespData.token } : {}),
    ...(signupRespData?.user ? { user: signupRespData.user } : {}),
  });

  try {
    await sendSignupOtps(signupData.email.trim(), fullPhone);
    toast.success('OTP sent to your email and phone');
  } catch (otpErr) {
    const msg =
      otpErr?.response?.data?.message ||
      otpErr?.response?.data?.errorMessage ||
      otpErr?.message ||
      'Failed to send OTP';
    console.warn('Send OTP:', otpErr?.response?.data || otpErr.message);
    toast.error(msg);
    return false;
  }

  sessionStorage.setItem(
    'signupData',
    JSON.stringify(buildSignupSessionData(isCompany, signupData, serviceTypeValue, fullPhone))
  );
  navigate('/verify?step=email');
  return true;
};

const computeSignupValidation = ({ accountType, signupData, signupAttempted, signupFieldTouched }) => {
  const isCompany = accountType === 'company';
  const cleanPhone = signupData.phone.replace(/\D/g, '').replace(/^0+/, '');
  const firstNameError = !isCompany && !signupData.firstName.trim();
  const lastNameError = !isCompany && !signupData.lastName.trim();
  const businessNameError = isCompany && !signupData.businessName.trim();
  const emailTrimmedForValidation = signupData.email.trim();
  const emailValidationResult = validateSignupEmail(emailTrimmedForValidation);
  const emailError = emailValidationResult !== null;
  const phoneEmpty = !signupData.phone.trim();
  const phoneInvalidFormat =
    signupData.phone.trim() && !/^[1-9]\d{7,11}$/.test(cleanPhone);
  const phoneError = phoneEmpty || phoneInvalidFormat;
  const countryError = !signupData.country;
  const typeError =
    signupData.contactType === '' ||
    signupData.contactType === null ||
    signupData.contactType === undefined;
  const passwordValidationResult = validatePassword(signupData.password);
  const passwordError =
    (signupAttempted || signupData.password.length > 0) && passwordValidationResult !== null;
  const canContinue = !(
    emailError ||
    phoneError ||
    countryError ||
    typeError ||
    passwordError ||
    (isCompany ? businessNameError : firstNameError || lastNameError) ||
    !signupData.termsAndConditionsAccepted ||
    !signupData.dataProcessingConsent
  );

  return {
    isCompany,
    firstNameError,
    lastNameError,
    businessNameError,
    emailValidationResult,
    emailError,
    phoneEmpty,
    phoneInvalidFormat,
    phoneError,
    countryError,
    typeError,
    passwordValidationResult,
    passwordError,
    canContinue,
    showFieldErrors: signupAttempted,
    showEmailValidation: signupAttempted || signupFieldTouched.email,
  };
};

const getSignupEmailHelperText = (showEmailValidation, emailValidationResult) => {
  if (!showEmailValidation) return undefined;
  if (emailValidationResult === 'required') return 'Email is required';
  if (emailValidationResult === 'tooLong') {
    return `Email must be at most ${SIGNUP_EMAIL_MAX} characters`;
  }
  if (emailValidationResult === 'invalid') {
    return 'Enter a valid email address (e.g. name@example.com). No spaces; use a real domain.';
  }
  return undefined;
};

const getSignupPasswordHelperText = (signupAttempted, password, passwordValidationResult) => {
  if (!signupAttempted && !password) return '';
  if (passwordValidationResult === 'required') return signupAttempted ? 'Password is required' : '';
  if (passwordValidationResult === 'tooShort') return 'Password must be at least 8 characters';
  if (passwordValidationResult === 'tooLong') return 'Password must be at most 12 characters';
  if (passwordValidationResult === 'weak') {
    return 'Must include upper, lower, number and special char (@$!%*?&#)';
  }
  return '';
};

const getSignupPhoneHelperText = (showFieldErrors, phoneEmpty, phoneInvalidFormat) => {
  if (!showFieldErrors) return '';
  if (phoneEmpty) return 'Phone number is required';
  if (phoneInvalidFormat) return 'Enter a valid German phone number (e.g. 17630992112)';
  return '';
};

const pickFirstCategorySelection = (categories) => {
  if (!Array.isArray(categories) || categories.length === 0) return null;
  const first = categories[0];
  return {
    contactType: first.id,
    contactTypeLabel: first.title_EN || first.title || first.name || '',
  };
};



const AuthLoginSection = ({
  loginForm,
  setLoginForm,
  showPassword,
  setShowPassword,
  loginError,
  setLoginError,
  rememberMe,
  setRememberMe,
  captchaCode,
  userCaptcha,
  setUserCaptcha,
  onRefreshCaptcha,
  captchaError,
  setCaptchaError,
  onSubmit,
  onForgotPassword,
  onSwitchToSignup,
  isLoading,
}) => (
  <>
    <Box component="form" onSubmit={onSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <TextField
        label="Email address"
        type="email"
        value={loginForm.email}
        onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })}
        required
        fullWidth
      />
      <TextField
        label="Password"
        type={showPassword ? 'text' : 'password'}
        value={loginForm.password}
        onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
        fullWidth
        InputProps={{
          endAdornment: (
            <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
              {showPassword ? <VisibilityOff /> : <Visibility />}
            </IconButton>
          ),
        }}
      />
      <Box sx={{ display: 'flex', gap: 1.25, alignItems: 'flex-start' }}>
        <TextField
          label="Enter CAPTCHA"
          size="small"
          value={userCaptcha}
          onChange={(e) => {
            setUserCaptcha(e.target.value);
            if (captchaError) setCaptchaError('');
          }}
          required
          fullWidth
          error={Boolean(captchaError)}
          helperText={captchaError}
          inputProps={{
            maxLength: 6,
            style: {
              letterSpacing: '2px',
              textTransform: 'uppercase',
              fontWeight: 600,
            },
          }}
          sx={{
            flex: 1.1,
            '& .MuiOutlinedInput-root': {
              borderRadius: '8px',
              '&:hover fieldset': { borderColor: '#FF8C42' },
              '&.Mui-focused fieldset': { borderColor: '#FF8C42' },
            },
          }}
        />
        <CaptchaCanvas code={captchaCode} onRefresh={onRefreshCaptcha} />
      </Box>
      {loginError && (
        <Typography color="error" variant="body2">
          {loginError}
        </Typography>
      )}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mt: 1,
          mb: 1,
        }}
      >
        <FormControlLabel
          control={
            <Checkbox
              size="small"
              checked={rememberMe}
              onChange={(e) => {
                const checked = e.target.checked;
                setRememberMe(checked);
                if (checked) {
                  setLoginError((prev) => (prev.includes('Remember me') ? '' : prev));
                }
              }}
            />
          }
          label={<Typography variant="body2">Remember me</Typography>}
        />
        <Button
          variant="text"
          size="small"
          onClick={onForgotPassword}
          sx={{ textTransform: 'none', fontWeight: 500, color: '#FF8C42', p: 0, minWidth: 0 }}
        >
          Forgot Password?
        </Button>
      </Box>
      <Button
        type="submit"
        variant="contained"
        fullWidth
        disabled={isLoading || !loginForm.email || !loginForm.password || !userCaptcha.trim()}
        sx={{
          mt: 1,
          py: 1.2,
          borderRadius: 2,
          fontWeight: 700,
          textTransform: 'none',
          backgroundColor: '#FF8C42',
          '&:hover': { backgroundColor: '#E06526' },
          '&.Mui-disabled': {
            backgroundColor: '#E2E8F0',
            color: '#94A3B8',
          },
        }}
      >
        {isLoading ? <CircularProgress size={24} sx={{ color: '#ffffff' }} /> : 'Sign in'}
      </Button>
    </Box>
    <Box textAlign="center" mt={3}>
      <Typography variant="body2" color="text.secondary">
        Don&apos;t have an account?{' '}
        <Button
          variant="text"
          size="small"
          onClick={onSwitchToSignup}
          sx={{ textTransform: 'none', fontWeight: 600, color: '#FF8C42', p: 0, minWidth: 0 }}
        >
          Sign up
        </Button>
      </Typography>
    </Box>
  </>
);

const buildContactTypeUpdate = (selectedId, categories) => {
  const found = categories.find((c) => String(c.id) === String(selectedId));
  const finalId = found ? found.id : selectedId;
  return {
    contactType: finalId,
    contactTypeLabel: found?.title_EN || found?.title || found?.name || '',
  };
};

const AuthSignupSection = ({
  // accountType,
  // setAccountType,
  signupData,
  setSignupData,
  signupAttempted,
  setSignupFieldTouched,
  isCompany,
  showFieldErrors,
  showEmailValidation,
  emailValidationResult,
  firstNameError,
  lastNameError,
  businessNameError,
  phoneError,
  phoneEmpty,
  phoneInvalidFormat,
  typeError,
  passwordError,
  passwordValidationResult,
  captchaCode,
  userCaptcha,
  setUserCaptcha,
  onRefreshCaptcha,
  captchaError,
  setCaptchaError,
  categories,
  categoriesLoading,
  categoriesError,
  showPassword,
  setShowPassword,
  onSubmit,
  onShowLegal,
  onSwitchToLogin,
  isLoading,
}) => (
  <>
    <Box component="form" onSubmit={onSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {/* <FormControl fullWidth>
        <InputLabel>Account Type</InputLabel>
        <Select value={accountType} label="Account Type" onChange={(e) => setAccountType(e.target.value)}>
          <MenuItem value="company">Company</MenuItem>
          <MenuItem value="freelancer">Freelancer</MenuItem>
        </Select>
      </FormControl> */}
      {isCompany ? (
        <TextField
          label="Business Name"
          placeholder="Enter your business name"
          value={signupData.businessName}
          onChange={(e) => setSignupData({ ...signupData, businessName: e.target.value })}
          required
          error={showFieldErrors && businessNameError}
          helperText={showFieldErrors && businessNameError ? 'Business name is required' : ''}
        />
      ) : (
        <Box sx={{ display: 'flex', gap: 2 }}>
          <TextField
            label="First Name"
            placeholder="First name"
            value={signupData.firstName}
            onChange={(e) => setSignupData({ ...signupData, firstName: e.target.value })}
            required
            error={showFieldErrors && firstNameError}
            helperText={showFieldErrors && firstNameError ? 'Required' : ''}
            sx={{ flex: 1 }}
          />
          <TextField
            label="Last Name"
            placeholder="Last name"
            value={signupData.lastName}
            onChange={(e) => setSignupData({ ...signupData, lastName: e.target.value })}
            required
            error={showFieldErrors && lastNameError}
            helperText={showFieldErrors && lastNameError ? 'Required' : ''}
            sx={{ flex: 1 }}
          />
        </Box>
      )}
      <TextField
        label="Email"
        type="email"
        name="email"
        autoComplete="email"
        placeholder="Enter your email"
        value={signupData.email}
        onChange={(e) =>
          setSignupData({
            ...signupData,
            email: e.target.value.replace(/\r|\n/g, ''),
          })
        }
        onBlur={() => {
          setSignupFieldTouched((t) => ({ ...t, email: true }));
          setSignupData((prev) => ({ ...prev, email: prev.email.trim() }));
        }}
        required
        error={showEmailValidation && emailValidationResult !== null}
        helperText={getSignupEmailHelperText(showEmailValidation, emailValidationResult)}
        inputProps={{ maxLength: SIGNUP_EMAIL_MAX }}
      />
      <TextField
        label="Phone Number"
        placeholder="176 30992112"
        value={signupData.phone}
        onChange={(e) => setSignupData({ ...signupData, phone: e.target.value.replace(/\D/g, '') })}
        required
        error={showFieldErrors && phoneError}
        helperText={getSignupPhoneHelperText(showFieldErrors, phoneEmpty, phoneInvalidFormat)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Typography sx={{ fontWeight: 600, color: '#334155', fontSize: '0.95rem' }}>
                +49
              </Typography>
            </InputAdornment>
          ),
        }}
      />
      <FormControl fullWidth error={showFieldErrors && typeError}>
        <InputLabel>Service Type</InputLabel>
        <Select
          value={signupData.contactType}
          label="Service Type"
          onChange={(e) => {
            setSignupData((prev) => ({
              ...prev,
              ...buildContactTypeUpdate(e.target.value, categories),
            }));
          }}
          required
          MenuProps={{ PaperProps: { sx: { maxHeight: 320 } } }}
        >
          <MenuItem value="">Select your service type</MenuItem>
          {categoriesLoading ? (
            <MenuItem value="" disabled>
              Loading...
            </MenuItem>
          ) : categoriesError ? (
            <MenuItem value="" disabled>
              {categoriesError}
            </MenuItem>
          ) : categories.length === 0 ? (
            <MenuItem value="" disabled>
              No categories found
            </MenuItem>
          ) : (
            categories.map((cat) => (
              <MenuItem key={cat.id} value={cat.id}>
                {cat.title_EN}
              </MenuItem>
            ))
          )}
        </Select>
        {showFieldErrors && typeError && <FormHelperText>Service type is required</FormHelperText>}
      </FormControl>
      <FormControl fullWidth>
        <InputLabel id="signup-country-label">Country</InputLabel>
        <Select
          labelId="signup-country-label"
          label="Country"
          value={signupData.country}
          onChange={(e) => setSignupData({ ...signupData, country: e.target.value })}
        >
          <MenuItem value="Germany">Germany</MenuItem>
        </Select>
      </FormControl>
      <TextField
        type={showPassword ? 'text' : 'password'}
        name="new-password"
        autoComplete="new-password"
        placeholder="Create a password"
        value={signupData.password}
        onChange={(e) => setSignupData({ ...signupData, password: e.target.value })}
        required
        error={passwordError}
        helperText={getSignupPasswordHelperText(
          signupAttempted,
          signupData.password,
          passwordValidationResult
        )}
        InputProps={{
          endAdornment: (
            <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
              {showPassword ? <VisibilityOff /> : <Visibility />}
            </IconButton>
          ),
        }}
        sx={{
          '& .MuiOutlinedInput-root': {
            borderRadius: 2,
            '&:hover fieldset': { borderColor: '#FF8C42' },
            '&.Mui-focused fieldset': { borderColor: '#FF8C42' },
          },
        }}
      />
      <Box sx={{ display: 'flex', gap: 1.25, alignItems: 'flex-start' }}>
        <TextField
          label="Enter CAPTCHA"
          size="small"
          value={userCaptcha}
          onChange={(e) => {
            setUserCaptcha(e.target.value);
            if (captchaError) setCaptchaError('');
          }}
          required
          fullWidth
          error={Boolean(captchaError)}
          helperText={captchaError}
          inputProps={{
            maxLength: 6,
            style: {
              letterSpacing: '2px',
              textTransform: 'uppercase',
              fontWeight: 600,
            },
          }}
          sx={{
            flex: 1.1,
            '& .MuiOutlinedInput-root': {
              borderRadius: '8px',
              '&:hover fieldset': { borderColor: '#FF8C42' },
              '&.Mui-focused fieldset': { borderColor: '#FF8C42' },
            },
          }}
        />
        <CaptchaCanvas code={captchaCode} onRefresh={onRefreshCaptcha} />
      </Box>
      <Box sx={{ mt: 1 }}>
        <FormControlLabel
          control={
            <Checkbox
              checked={signupData.termsAndConditionsAccepted}
              onChange={(e) =>
                setSignupData((prev) => ({ ...prev, termsAndConditionsAccepted: e.target.checked }))
              }
            />
          }
          label={
            <Typography variant="body2">
              I agree to the{' '}
              <Typography
                component="span"
                variant="body2"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onShowLegal('terms');
                }}
                sx={{ textDecoration: 'underline', cursor: 'pointer', color: '#FF8C42', fontWeight: 600 }}
              >
                Terms & Conditions
              </Typography>
            </Typography>
          }
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={signupData.dataProcessingConsent}
              onChange={(e) =>
                setSignupData((prev) => ({ ...prev, dataProcessingConsent: e.target.checked }))
              }
            />
          }
          label={
            <Typography variant="body2">
              I agree to the Data Processing Consent and{' '}
              <Typography
                component="span"
                variant="body2"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onShowLegal('privacy');
                }}
                sx={{ textDecoration: 'underline', cursor: 'pointer', color: '#FF8C42', fontWeight: 600 }}
              >
                Privacy Policy
              </Typography>
            </Typography>
          }
        />
        {showFieldErrors && !signupData.termsAndConditionsAccepted && (
          <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>
            Please accept the Terms & Conditions
          </Typography>
        )}
        {showFieldErrors && !signupData.dataProcessingConsent && (
          <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>
            Please accept the Data Processing Consent & Privacy Policy
          </Typography>
        )}
      </Box>
      <Button
        variant="contained"
        type="submit"
        fullWidth
        disabled={isLoading || !userCaptcha.trim()}
        sx={{
          mt: 1,
          py: 1.1,
          borderRadius: 2,
          fontWeight: 700,
          textTransform: 'none',
          backgroundColor: '#FF8C42',
          '&:hover': { backgroundColor: '#E06526' },
          '&.Mui-disabled': {
            backgroundColor: '#E2E8F0',
            color: '#94A3B8',
          },
        }}
      >
        {isLoading ? <CircularProgress size={24} sx={{ color: '#ffffff' }} /> : 'Sign up'}
      </Button>
    </Box>
    <Box textAlign="center" mt={3}>
      <Typography variant="body2" color="text.secondary">
        Already have an account?{' '}
        <Button
          variant="text"
          size="small"
          onClick={onSwitchToLogin}
          sx={{ textTransform: 'none', fontWeight: 600, color: '#FF8C42', p: 0, minWidth: 0 }}
        >
          Sign in
        </Button>
      </Typography>
    </Box>
  </>
);

const getAuthPageSubtitle = (tab) =>
  tab === 'login' ? 'Sign in to your account' : 'Create your Settled account';

const decodeHtmlEntities = (str) => {
  if (!str) return '';
  if (str.includes('&lt;') || str.includes('&gt;') || str.includes('&amp;') || str.includes('&quot;')) {
    const txt = document.createElement('textarea');
    txt.innerHTML = str;
    return txt.value;
  }
  return str;
};

const fetchLegalDocument = async (type) => {
  const response = type === 'terms' ? await getLatestTerms() : await getLatestPrivacy();
  if (!response) {
    return 'No response from server.';
  }
  if (typeof response.data === 'string' && response.data.trim()) {
    return response.data;
  }
  const content =
    response?.data?.payload?.content ||
    response?.data?.payload ||
    response?.data?.data?.content ||
    response?.data?.data ||
    response?.data?.content;

  if (typeof content === 'string') {
    return content;
  }
  if (content && typeof content === 'object') {
    return content.content || content.text || JSON.stringify(content);
  }
  return 'No content available.';
};

const LegalDocumentDialog = ({ legalModal, onClose }) => {
  const decodedContent = decodeHtmlEntities(legalModal.content);

  return (
    <Dialog
      open={legalModal.open}
      onClose={onClose}
      fullScreen
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'center',
          backgroundColor: '#f8fafc',
          borderBottom: '1px solid #e2e8f0',
          px: 3,
          py: 1,
        }}
      >
        <IconButton onClick={onClose} size="medium" sx={{ color: '#64748b' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent
        dividers
        sx={{
          p: { xs: 3, md: 5 },
          backgroundColor: '#ffffff',
        }}
      >
        {legalModal.loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
            <CircularProgress sx={{ color: '#FF8C42' }} />
          </Box>
        ) : (
          <Box
            className="legal-content-rendered"
            sx={{
              '& h1': { fontSize: '1.8rem', fontWeight: 700, color: '#1E3A45', mt: 3, mb: 1.5 },
              '& h2': { fontSize: '1.4rem', fontWeight: 600, color: '#1E3A45', mt: 2.5, mb: 1.2 },
              '& h3': { fontSize: '1.2rem', fontWeight: 600, color: '#1E3A45', mt: 2, mb: 1 },
              '& p': { mb: 2, lineHeight: 1.7, color: '#334155', fontSize: '0.975rem' },
              '& ul, & ol': { mb: 2, pl: 3.5, lineHeight: 1.7, color: '#334155' },
              '& li': { mb: 0.8 },
              maxWidth: '720px',
              margin: '0 auto',
            }}
            dangerouslySetInnerHTML={{ __html: decodedContent }}
          />
        )}
      </DialogContent>
    </Dialog>
  );
};


const AuthPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();

  const [tab, setTab] = useState('login');
  const [showPassword, setShowPassword] = useState(false);

  // Login CAPTCHA state
  const [loginCaptchaCode, setLoginCaptchaCode] = useState(() => generateCaptchaCode());
  const [loginUserCaptcha, setLoginUserCaptcha] = useState('');
  const [loginCaptchaError, setLoginCaptchaError] = useState('');

  // Signup CAPTCHA state
  const [signupCaptchaCode, setSignupCaptchaCode] = useState(() => generateCaptchaCode());
  const [signupUserCaptcha, setSignupUserCaptcha] = useState('');
  const [signupCaptchaError, setSignupCaptchaError] = useState('');

  const refreshLoginCaptcha = () => {
    setLoginCaptchaCode(generateCaptchaCode());
    setLoginUserCaptcha('');
    setLoginCaptchaError('');
  };

  const refreshSignupCaptcha = () => {
    setSignupCaptchaCode(generateCaptchaCode());
    setSignupUserCaptcha('');
    setSignupCaptchaError('');
  };

  // Login state (prefill email after password reset from ForgotPassword)
  const [loginForm, setLoginForm] = useState(() => {
    const fromReset = location.state?.loginEmail;
    const prefill = typeof fromReset === 'string' && fromReset.trim() ? fromReset.trim() : '';
    return { email: prefill, password: '' };
  });
  const [loginError, setLoginError] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [loginLoading, setLoginLoading] = useState(false);
  const [signupLoading, setSignupLoading] = useState(false);

  // Signup state
  const getInitialSignupData = () => ({
    businessName: '',
    name: '',
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    country: 'Germany',
    contactType: '',
    contactTypeLabel: '',
    password: '',
    termsAndConditionsAccepted: false,
    dataProcessingConsent: false,
  });
  const [accountType, setAccountType] = useState('company');
  const [signupData, setSignupData] = useState(getInitialSignupData);
  const [signupAttempted, setSignupAttempted] = useState(false);
  /** After blur (or submit), show signup email format/required errors without waiting for Continue. */
  const [signupFieldTouched, setSignupFieldTouched] = useState({ email: false });
  const [categories, setCategories] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  const [categoriesError, setCategoriesError] = useState('');
  
  // Legal Dialog state
  const [legalModal, setLegalModal] = useState({
    open: false,
    title: '',
    content: '',
    loading: false,
  });

  const resetSignupForm = () => {
    setAccountType('company');
    setSignupData(getInitialSignupData());
    setSignupAttempted(false);
    setSignupFieldTouched({ email: false });
    refreshSignupCaptcha();
  };

  // Validation helpers
  const {
    isCompany,
    firstNameError,
    lastNameError,
    businessNameError,
    emailValidationResult,
    emailError,
    phoneEmpty,
    phoneInvalidFormat,
    phoneError,
    countryError,
    typeError,
    passwordValidationResult,
    passwordError,
    canContinue,
    showFieldErrors,
    showEmailValidation,
  } = computeSignupValidation({ accountType, signupData, signupAttempted, signupFieldTouched });

  useEffect(() => {
    if (tab !== 'signup') return;

    let isCancelled = false;
    const loadCategories = async () => {
      setCategoriesLoading(true);
      setCategoriesError('');
      try {
        const response = await categoriesAPI.getCategories();
        const list = response?.data?.payload || response?.data?.data || response?.data || [];
        const normalized = Array.isArray(list) ? list : [];

        if (!isCancelled) {
          setCategories(normalized);

          // Auto-select first category (helps users not get stuck on required field).
          if (signupData.contactType === '' || signupData.contactType === undefined) {
            const firstSelection = pickFirstCategorySelection(normalized);
            if (firstSelection) {
              setSignupData((prev) => ({ ...prev, ...firstSelection }));
            }
          }
        }
      } catch (e) {
        if (!isCancelled) setCategoriesError(e?.message || 'Failed to load categories');
      } finally {
        if (!isCancelled) setCategoriesLoading(false);
      }
    };

    loadCategories();
    return () => {
      isCancelled = true;
    };
    // We intentionally don't include `signupData` in deps to avoid re-fetching while typing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError('');
    setLoginCaptchaError('');

    if (!loginUserCaptcha.trim()) {
      setLoginCaptchaError('CAPTCHA code is required');
      return;
    }
    if (loginUserCaptcha.trim().toUpperCase() !== loginCaptchaCode.toUpperCase()) {
      setLoginCaptchaError('Incorrect CAPTCHA code');
      toast.error('Incorrect CAPTCHA code. Please try again.');
      refreshLoginCaptcha();
      return;
    }

    setLoginLoading(true);
    try {
      const response = await login(loginForm.email, loginForm.password);
      if (response?.success === true) {
        const status = pickLoginVerificationStatus(response);
        const { severity, message } = getLoginSuccessToastMessage(status);
        toast[severity](message);
      } else {
        throw new Error('Invalid response format');
      }
    } catch (err) {
      setLoginError(resolveLoginErrorMessage(err));
      refreshLoginCaptcha();
    } finally {
      setLoginLoading(false);
    }
  };

  const handleSignupContinue = async (e) => {
    e.preventDefault();
    setSignupAttempted(true);
    setSignupCaptchaError('');

    const hasFieldErrors =
      emailError ||
      phoneError ||
      countryError ||
      typeError ||
      passwordError ||
      (isCompany ? businessNameError : firstNameError || lastNameError);

    if (hasFieldErrors) {
      toast.error('Please fill in all required fields correctly.');
      return;
    }

    if (!signupUserCaptcha.trim()) {
      setSignupCaptchaError('CAPTCHA code is required');
      toast.error('CAPTCHA code is required');
      return;
    }
    if (signupUserCaptcha.trim().toUpperCase() !== signupCaptchaCode.toUpperCase()) {
      setSignupCaptchaError('Incorrect CAPTCHA code');
      toast.error('Incorrect CAPTCHA code. Please try again.');
      refreshSignupCaptcha();
      return;
    }

    if (!signupData.termsAndConditionsAccepted || !signupData.dataProcessingConsent) {
      return;
    }

    setSignupLoading(true);
    try {
      const serviceTypeValue = normalizeServiceType(signupData.contactType);
      const cleanPhone = signupData.phone.replace(/\D/g, '').replace(/^0+/, '');
      const fullPhone = `+49${cleanPhone}`;
      const payload = buildSignupPayload(isCompany, signupData, fullPhone, serviceTypeValue);
      const { signupRespData, errorMessage } = await submitSignupRequest(payload);

      if (errorMessage) {
        if (errorMessage.toLowerCase().includes('account already exists')) {
          redirectExistingAccountToLogin(setTab, setLoginForm);
          return;
        }
        toast.error(errorMessage);
        return;
      }

      const existingUserHandled = processExistingUserSignupResponse(signupRespData, {
        isCompany,
        signupData,
        serviceTypeValue,
        navigate,
        setTab,
        setLoginForm,
      });
      if (existingUserHandled) {
        return;
      }

      await completeNewUserSignup({
        signupRespData,
        signupData,
        isCompany,
        serviceTypeValue,
        fullPhone,
        navigate,
      });
    } catch (err) {
      const msg = err.response?.data?.message || err.response?.data?.errorMessage || 'Something went wrong';
      toast.error(msg);
    } finally {
      setSignupLoading(false);
    }
  };

  const handleShowLegal = async (type) => {
    setLegalModal({
      open: true,
      title: type === 'terms' ? 'Terms & Conditions' : 'Privacy Policy',
      content: '',
      loading: true,
    });

    try {
      const content = await fetchLegalDocument(type);
      setLegalModal((prev) => ({ ...prev, content, loading: false }));
    } catch (err) {
      console.error(`Failed to fetch ${type}:`, err);
      toast.error(`Failed to load ${type}. Please try again later.`);
      setLegalModal((prev) => ({ ...prev, loading: false, content: 'Error loading content.' }));
    }
  };

  const closeLegalModal = () => setLegalModal((prev) => ({ ...prev, open: false }));

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
        p: 2,
      }}
    >
      <Card sx={{ width: '100%', maxWidth: 460, borderRadius: 4, boxShadow: '0 10px 35px rgba(15,23,42,0.18)' }}>
        <CardContent sx={{ p: 4 }}>
          {/* Logo + title */}
          <Box sx={{ textAlign: 'center', mb: 3, mt: -2 }}>
            <Box
              component="img"
              src={logo}
              alt="Logo"
              sx={{
                width: 170,
                height: 'auto',
                objectFit: 'contain',
                mb: 1.5,
              }}
            />

            <Typography
              variant="body2"
              sx={{
                color: '#161718',   // medium dark text
                fontWeight: 500,
                fontSize: '1.25rem',
              }}
            >
              {getAuthPageSubtitle(tab)}
            </Typography>
          </Box>


          {tab === 'login' ? (
            <AuthLoginSection
              loginForm={loginForm}
              setLoginForm={setLoginForm}
              showPassword={showPassword}
              setShowPassword={setShowPassword}
              loginError={loginError}
              setLoginError={setLoginError}
              rememberMe={rememberMe}
              setRememberMe={setRememberMe}
              captchaCode={loginCaptchaCode}
              userCaptcha={loginUserCaptcha}
              setUserCaptcha={setLoginUserCaptcha}
              onRefreshCaptcha={refreshLoginCaptcha}
              captchaError={loginCaptchaError}
              setCaptchaError={setLoginCaptchaError}
              onSubmit={handleLogin}
              onForgotPassword={() => navigate('/forgot-password')}
              onSwitchToSignup={() => {
                resetSignupForm();
                setTab('signup');
              }}
              isLoading={loginLoading}
            />
          ) : (
            <AuthSignupSection
              // accountType={accountType}
              // setAccountType={setAccountType}
              signupData={signupData}
              setSignupData={setSignupData}
              signupAttempted={signupAttempted}
              setSignupFieldTouched={setSignupFieldTouched}
              isCompany={isCompany}
              showFieldErrors={showFieldErrors}
              showEmailValidation={showEmailValidation}
              emailValidationResult={emailValidationResult}
              firstNameError={firstNameError}
              lastNameError={lastNameError}
              businessNameError={businessNameError}
              phoneError={phoneError}
              phoneEmpty={phoneEmpty}
              phoneInvalidFormat={phoneInvalidFormat}
              typeError={typeError}
              passwordError={passwordError}
              passwordValidationResult={passwordValidationResult}
              captchaCode={signupCaptchaCode}
              userCaptcha={signupUserCaptcha}
              setUserCaptcha={setSignupUserCaptcha}
              onRefreshCaptcha={refreshSignupCaptcha}
              captchaError={signupCaptchaError}
              setCaptchaError={setSignupCaptchaError}
              categories={categories}
              categoriesLoading={categoriesLoading}
              categoriesError={categoriesError}
              showPassword={showPassword}
              setShowPassword={setShowPassword}
              onSubmit={handleSignupContinue}
              onShowLegal={handleShowLegal}
              onSwitchToLogin={() => {
                refreshLoginCaptcha();
                setTab('login');
              }}
              isLoading={signupLoading}
            />
          )}
        </CardContent>
      </Card>

      <LegalDocumentDialog legalModal={legalModal} onClose={closeLegalModal} />
    </Box>
  );
};

export default AuthPage;
